import type {
  Divergence,
  InterviewPanel,
  KnownRead,
  Probe,
} from "@shared/labs/level-assessment-interview-panel";
import { ArrowRight, ChevronDown, PieChart } from "lucide-react";
import { ConfChip, LevelChip } from "../../results/chips";
import { LevelWhisker } from "../../results/level-whisker";
import { C, DM, MONO, UI } from "../../results/ui-tokens";
import { Card, fmtDate } from "../../sets/ui";
import { Avatar, DisciplineChip, Skeleton } from "../candidate-ui";
import type { ClarifyCard, OpenItem, ResolvedItem } from "./derive";
import {
  BlindSpotBadge,
  DecisiveSignalBox,
  EvidenceChip,
  IndirectSignalPill,
  SectionEyebrow,
  StatusWordText,
  VIOLET,
  VIOLET_BD,
  VIOLET_BG,
} from "./ui";

/** Format a level number for a big display, or a dash. */
function fmtNum(n: number | null, digits = 1): string {
  return n === null ? "—" : n.toFixed(digits).replace(".", ",");
}

// ── Header (single dense card, matching the rounds-overview header) ───────────

/** How the estimate moved since the previous scored round — plain text, no
 *  chip chrome (chips encode values; a reading is a sentence). */
function evolutionLine(
  previous: NonNullable<InterviewPanel["previousRound"]>,
  current: { level: number | null; confidence: number | null },
): string {
  const prevLevel = previous.level === null ? "—" : `L${previous.level}`;
  const currLevel = current.level === null ? "—" : `L${current.level}`;
  const prevConf = previous.confidence === null ? "—" : `${Math.round(previous.confidence * 100)}%`;
  const currConf = current.confidence === null ? "—" : `${Math.round(current.confidence * 100)}%`;
  const roundLabel =
    previous.roundLabel ??
    (previous.roundSequence ? `Rodada ${previous.roundSequence}` : "rodada anterior");
  return `desde a ${roundLabel}: ${prevLevel} → ${currLevel} · confiança ${prevConf} → ${currConf}`;
}

export function PanelHeader({ panel, blind = false }: { panel: InterviewPanel; blind?: boolean }) {
  const { candidate, meta, currentLevel, whisker, dispersion, confidence, levels, previousRound } =
    panel;
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.ink10}`,
        borderRadius: 18,
        padding: "20px 24px",
        marginTop: 22,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        {/* Identity */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, minWidth: 0 }}>
          <Avatar name={candidate.name} size={56} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <div
                style={{
                  ...DM,
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  color: C.ink,
                }}
              >
                {candidate.name}
              </div>
              {candidate.disciplineLabel && <DisciplineChip label={candidate.disciplineLabel} />}
              <StatusWordPlain status={candidate.status} />
            </div>
            {candidate.subtitle && (
              <div style={{ ...UI, fontSize: 13.5, color: C.ink50, marginTop: 2 }}>
                {candidate.subtitle}
              </div>
            )}
            <div style={{ ...MONO, fontSize: 12.5, color: C.ink50, marginTop: 7 }}>
              modelo {meta.modelName} · rodada {meta.roundSequence}/{meta.roundsTotal} ·{" "}
              {fmtDate(meta.sinceDate)}
            </div>
          </div>
        </div>

        {/* Current level — hidden in blind mode (the interviewer measures first) */}
        {!blind && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span
              style={{
                ...UI,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0",
                textTransform: "uppercase",
                color: C.red,
              }}
            >
              Nível atual
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 12,
                marginTop: 6,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <span
                style={{
                  ...DM,
                  fontSize: 38,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: C.ink,
                }}
              >
                {currentLevel.level === null ? "—" : `L${currentLevel.level}`}
              </span>
              {currentLevel.levelName && (
                <span style={{ ...DM, fontSize: 18, fontWeight: 600, color: C.ink70 }}>
                  {currentLevel.levelName}
                </span>
              )}
            </div>
            {currentLevel.carriedFromRound !== null && (
              <span
                style={{
                  ...MONO,
                  fontSize: 11.5,
                  color: VIOLET,
                  background: VIOLET_BG,
                  border: `1px solid ${VIOLET_BD}`,
                  borderRadius: 999,
                  padding: "3px 9px",
                  marginTop: 2,
                }}
              >
                herdado da rodada {currentLevel.carriedFromRound}
              </span>
            )}
          </div>
        )}
      </div>

      {!blind && (
        <>
          {/* Rail: the fluid level bar absorbs the free width; the plain-text
           *  evolution + estimate readings stay pinned to the right edge (same
           *  composition as the rounds-overview header, text where its chips sat). */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
              marginTop: 16,
              paddingTop: 16,
              borderTop: `1px solid ${C.ink10}`,
            }}
          >
            <div style={{ flex: "1 1 260px", minWidth: 220 }}>
              <LevelWhisker
                mean={whisker.mean}
                stddev={whisker.stddev}
                levels={levels}
                size="lg"
                width={360}
                marker={previousRound?.level != null ? { level: previousRound.level } : null}
                fluid
              />
            </div>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 4,
              }}
            >
              {previousRound ? (
                <span style={{ ...MONO, fontSize: 12.5, color: C.ink70 }}>
                  {evolutionLine(previousRound, { level: currentLevel.level, confidence })}
                </span>
              ) : (
                confidence !== null && (
                  <span style={{ ...MONO, fontSize: 12.5, color: C.ink70 }}>
                    confiança média {Math.round(confidence * 100)}%
                  </span>
                )
              )}
              {whisker.mean !== null && (
                <span style={{ ...MONO, fontSize: 12, color: C.ink50 }}>
                  estimativa média {fmtNum(whisker.mean)}
                  {dispersion.rangeLabel && ` · banda ${dispersion.rangeLabel}`}
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Candidate lifecycle as a plain colored word (Word-Carries-State). */
function StatusWordPlain({ status }: { status: "active" | "archived" }) {
  const active = status === "active";
  return (
    <span
      style={{
        ...UI,
        fontSize: 12,
        fontWeight: 600,
        color: active ? C.green : C.ink50,
      }}
    >
      {active ? "Ativo" : "Arquivado"}
    </span>
  );
}

// ── Act 1 · Evidence base ────────────────────────────────────────────────────

/** The accumulated evidence artifacts + the coverage read. Rendered as the
 *  first block inside Act 1 (the act header does the heavy lifting; this
 *  keeps only a small sub-eyebrow). */
export function EvidenceInventory({ panel }: { panel: InterviewPanel }) {
  const { packets, lowCoverageBanner } = panel.evidence;
  return (
    <div style={{ marginTop: 20 }}>
      <SectionEyebrow>Evidências</SectionEyebrow>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        {packets.map((p) => (
          <EvidenceChip
            key={`${p.type}-${p.roundLabel}-${p.label}`}
            type={p.type}
            label={p.label}
            roundLabel={p.roundLabel}
            scored={p.scored}
          />
        ))}
      </div>

      {panel.coverageState === "low" && lowCoverageBanner && (
        <div
          className="avoid-break"
          style={{
            marginTop: 14,
            background: C.red5,
            border: `1px solid ${C.red10}`,
            borderRadius: 14,
            padding: "15px 17px",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <PieChart size={18} style={{ color: C.red, marginTop: 2, flex: "0 0 auto" }} />
          <div>
            <div
              style={{
                ...UI,
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: "0",
                textTransform: "uppercase",
                color: C.red,
              }}
            >
              {lowCoverageBanner.headline}
            </div>
            <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.55, marginTop: 4 }}>
              {lowCoverageBanner.subtext}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Act 1 · O que já sabemos ─────────────────────────────────────────────────

function KnownReadCard({ read }: { read: KnownRead }) {
  const hasSignals = read.decisiveSignals.length > 0 || read.indirect;

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
      <LevelChip level={read.level === null ? "—" : `L${read.level}`} />
      <span style={{ ...DM, fontSize: 16, fontWeight: 700, color: C.ink, flex: 1, minWidth: 0 }}>
        {read.axisLabel}
      </span>
    </div>
  );

  // No individual signals to expand → keep the card as a plain, always-open summary.
  if (!hasSignals) {
    return (
      <Card className="avoid-break" style={{ padding: 18 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          {header}
          <ConfChip value={read.confidence} small />
        </div>
        {read.summary && (
          <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.55, marginTop: 10 }}>
            {read.summary}
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card className="avoid-break" style={{ padding: 0 }}>
      <details>
        <summary
          style={{
            listStyle: "none",
            cursor: "pointer",
            padding: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            {header}
            <div
              style={{ display: "inline-flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}
            >
              <ConfChip value={read.confidence} small />
              <ChevronDown size={16} className="chev" style={{ color: C.ink50 }} />
            </div>
          </div>
          {read.summary && (
            <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.55, marginTop: 10 }}>
              {read.summary}
            </div>
          )}
        </summary>
        <div style={{ padding: "0 18px 18px" }}>
          {read.indirect && read.decisiveSignals.length === 0 ? (
            <IndirectSignalPill />
          ) : (
            read.decisiveSignals.map((s) => (
              <DecisiveSignalBox key={`${s.title}-${s.value}`} signal={s} />
            ))
          )}
        </div>
      </details>
    </Card>
  );
}

function KnownGroup({ label, reads }: { label: string; reads: KnownRead[] }) {
  if (reads.length === 0) return null;
  return (
    <div style={{ marginTop: 22 }}>
      <div
        style={{
          ...MONO,
          fontSize: 11.5,
          letterSpacing: "0",
          textTransform: "uppercase",
          color: C.ink50,
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <div className="know-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
        {reads.map((r) => (
          <KnownReadCard key={r.axisId} read={r} />
        ))}
      </div>
    </div>
  );
}

export function KnownReads({ panel }: { panel: InterviewPanel }) {
  const { cross, vertical } = panel.known;
  if (cross.length === 0 && vertical.length === 0) return null;
  return (
    <div>
      <KnownGroup label="Transversais" reads={cross} />
      <KnownGroup label="Da disciplina" reads={vertical} />
    </div>
  );
}

// ── Act 2 · O que a entrevista precisa esclarecer ────────────────────────────

function BoundaryPill({ label, accent }: { label: string; accent?: boolean }) {
  return (
    <span
      style={{
        ...MONO,
        fontSize: 12,
        fontWeight: accent ? 600 : 400,
        color: accent ? C.red : C.ink50,
        border: `1px solid ${accent ? C.red20 : C.ink10}`,
        borderRadius: 999,
        padding: "3px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function SubLabel({ children }: { children: string }) {
  return (
    <div
      style={{
        ...UI,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: "0",
        textTransform: "uppercase",
        color: C.ink50,
      }}
    >
      {children}
    </div>
  );
}

/** One still-open question inside the card: its own title + gap-type chip,
 *  the point's probe, and (when a previous round touched it) a plain-word
 *  partial-progress line. */
function OpenItemRow({ item, last }: { item: OpenItem; last: boolean }) {
  return (
    <div
      style={{
        padding: "10px 0",
        borderBottom: last ? "none" : `1px solid ${C.borderSoft}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{ ...DM, fontSize: 13.5, fontWeight: 700, color: C.ink, flex: 1, minWidth: 0 }}
        >
          {item.title}
        </span>
        <BlindSpotBadge type={item.type} />
      </div>
      {item.probe && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 7 }}>
          <span
            style={{ ...MONO, fontSize: 12.5, fontWeight: 600, color: C.red, flex: "0 0 auto" }}
          >
            ↳
          </span>
          <span style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5 }}>
            {item.probe}
          </span>
        </div>
      )}
      {item.partial && (
        <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 6 }}>
          parcialmente endereçado{item.partial.roundLabel ? ` na ${item.partial.roundLabel}` : ""}
          {item.partial.outcome && (
            <>
              {" · "}
              <StatusWordText word={item.partial.outcome} size={12} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Client-facing label for a packet type — never show the raw enum token. */
const PACKET_TYPE_LABEL: Record<string, string> = {
  linkedin: "Perfil LinkedIn",
  cv: "Currículo",
  interview_transcript: "Transcrição de entrevista",
  free_text: "Texto livre",
};

function packetTypeLabel(type: string): string {
  return PACKET_TYPE_LABEL[type] ?? "Evidência";
}

/** The verbatim a-verificar quotes attached to this competency — evidence the
 *  raw text suggested but the score does not yet reflect. */
function QuotesBlock({ quotes }: { quotes: Divergence[] }) {
  return (
    <div
      className="avoid-break"
      style={{
        marginTop: 14,
        background: VIOLET_BG,
        border: `1px solid ${VIOLET_BD}`,
        borderRadius: 12,
        padding: "13px 15px",
      }}
    >
      <div
        style={{
          ...UI,
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0",
          textTransform: "uppercase",
          color: VIOLET,
        }}
      >
        O que o texto sugeriu
      </div>
      {quotes.map((d, i) => (
        <div
          key={`${d.sourcePacket.roundLabel}-${d.finding}`}
          style={{
            paddingTop: 10,
            marginTop: 10,
            borderTop: i === 0 ? "none" : `1px solid ${VIOLET_BD}`,
          }}
        >
          <span style={{ ...MONO, fontSize: 11.5, color: VIOLET }}>
            {packetTypeLabel(d.sourcePacket.type)} · {d.sourcePacket.roundLabel}
          </span>
          <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.55, marginTop: 5 }}>
            {d.finding}
          </div>
          {d.quote && (
            <blockquote
              style={{
                ...UI,
                margin: "7px 0 0",
                fontSize: 13,
                color: C.ink70,
                lineHeight: 1.55,
                background: C.white,
                borderLeft: `3px solid ${VIOLET}`,
                borderRadius: "0 8px 8px 0",
                padding: "8px 12px",
              }}
            >
              "{d.quote}"
            </blockquote>
          )}
        </div>
      ))}
      <div style={{ ...UI, fontSize: 12, fontWeight: 600, color: VIOLET, marginTop: 12 }}>
        A verificar na entrevista
      </div>
    </div>
  );
}

/** The prescription half of the card, visually tied to the open points above
 *  it: "these are the gaps → this is how the interview closes them". */
function HowToClarify({ probe }: { probe: Probe }) {
  return (
    <div
      style={{
        marginTop: 14,
        background: C.paper,
        borderRadius: 12,
        padding: "13px 15px",
      }}
    >
      <div
        style={{
          ...UI,
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0",
          textTransform: "uppercase",
          color: C.ink70,
        }}
      >
        Para a entrevista · {probe.axisLabel}
      </div>
      {probe.whatToListen && (
        <div style={{ marginTop: 10 }}>
          <SubLabel>O que ouvir</SubLabel>
          <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.55, marginTop: 5 }}>
            {probe.whatToListen}
          </div>
        </div>
      )}
      {probe.resolvingEvidence && (
        <div style={{ marginTop: 12 }}>
          <SubLabel>Evidência que resolve</SubLabel>
          <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.55, marginTop: 5 }}>
            {probe.resolvingEvidence}
          </div>
        </div>
      )}
      {probe.suggestedQuestions.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <SubLabel>Perguntas sugeridas</SubLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {probe.suggestedQuestions.map((q, i) => (
              <div
                key={q}
                style={{
                  display: "flex",
                  gap: 10,
                  background: C.white,
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                <span style={{ ...MONO, fontSize: 12, color: C.red, fontWeight: 600 }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span style={{ ...DM, fontSize: 13.5, color: C.ink, lineHeight: 1.5 }}>{q}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClarifyCardView({
  card,
  blind = false,
  dense = false,
}: {
  card: ClarifyCard;
  blind?: boolean;
  /** Glance mode (the candidate Visão geral): open points inline, no quotes
   *  block and no full prescription — that depth lives in the round briefing.
   *  The list reads as a list instead of N repeated dossiers. */
  dense?: boolean;
}) {
  const openCount = card.openItems.length;
  const hasBody =
    openCount > 0 ||
    Boolean(card.body || card.detail) ||
    card.probe !== null ||
    card.quotes.length > 0;

  const head = (
    <>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...DM, fontSize: 16, fontWeight: 700, color: C.ink, display: "block" }}>
          {card.axisLabel}
        </span>
        {openCount > 0 && (
          <span
            style={{
              ...UI,
              display: "block",
              fontSize: 11.5,
              fontWeight: 500,
              color: C.ink50,
              marginTop: 2,
            }}
          >
            {openCount} {openCount === 1 ? "ponto em aberto" : "pontos em aberto"}
          </span>
        )}
      </span>
      {card.probe && !blind ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flex: "0 0 auto" }}>
          <BoundaryPill label={card.probe.boundary.from} />
          <ArrowRight size={14} style={{ color: C.red }} />
          <BoundaryPill label={card.probe.boundary.to} accent />
        </span>
      ) : (
        // In dense mode each visible open point carries its own action chip, so
        // the card-level summary chip would just repeat it — omit it there.
        !dense && card.type && <BlindSpotBadge type={card.type} />
      )}
    </>
  );

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    listStyle: "none",
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "14px 16px",
  } as const;

  if (!hasBody) {
    return (
      <div className="avoid-break" style={{ ...rowStyle, marginBottom: 10 }}>
        {head}
      </div>
    );
  }

  // Glance mode: one bordered card, open points inline, no disclosure and no
  // prescription/quotes scaffold. The overview is a list, not N dossiers.
  if (dense) {
    return (
      <div
        className="avoid-break"
        style={{
          background: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "14px 16px",
          }}
        >
          {head}
        </div>
        {openCount > 0 && (
          <div style={{ padding: "0 16px 8px 16px" }}>
            {card.openItems.map((item, i) => (
              <OpenItemRow key={item.id} item={item} last={i === openCount - 1} />
            ))}
          </div>
        )}
        {openCount === 0 && card.body && (
          <div
            style={{
              ...UI,
              fontSize: 13.5,
              color: C.ink70,
              lineHeight: 1.55,
              padding: "0 16px 14px 16px",
            }}
          >
            {card.body}
          </div>
        )}
      </div>
    );
  }

  return (
    <details className="avoid-break" style={{ marginBottom: 10 }}>
      <summary style={{ ...rowStyle, cursor: "pointer" }}>
        {head}
        <ChevronDown size={16} className="chev" style={{ color: C.ink50, flex: "0 0 auto" }} />
      </summary>
      <div style={{ padding: "12px 16px 6px 16px" }}>
        {openCount > 0 && (
          <div>
            {card.openItems.map((item, i) => (
              <OpenItemRow key={item.id} item={item} last={i === openCount - 1} />
            ))}
          </div>
        )}

        {openCount === 0 && card.body && (
          <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.55, marginTop: 10 }}>
            {card.body}
          </div>
        )}
        {openCount === 0 && card.detail && (
          <div
            style={{
              marginTop: 10,
              background: C.ink5,
              borderRadius: 12,
              padding: "11px 13px",
              ...UI,
              fontSize: 13,
              color: C.ink70,
              lineHeight: 1.55,
            }}
          >
            {card.detail.kind === "note" ? (
              card.detail.text
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {card.detail.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {card.quotes.length > 0 && <QuotesBlock quotes={card.quotes} />}
        {card.probe && <HowToClarify probe={card.probe} />}
      </div>
    </details>
  );
}

export function ClarifySection({
  cards,
  blind = false,
  dense = false,
}: {
  cards: ClarifyCard[];
  blind?: boolean;
  /** Glance mode for the candidate Visão geral (see {@link ClarifyCardView}). */
  dense?: boolean;
}) {
  if (cards.length === 0) {
    return (
      <div style={{ ...UI, fontSize: 13.5, color: C.ink50, marginTop: 20 }}>
        Nenhum ponto em aberto.
      </div>
    );
  }
  return (
    <div style={{ marginTop: dense ? 0 : 20 }}>
      {cards.map((card) => (
        <ClarifyCardView key={card.axisId} card={card} blind={blind} dense={dense} />
      ))}
    </div>
  );
}

// ── Act 3 · O que já foi esclarecido ─────────────────────────────────────────

/** A quiet history list: one row per settled point, its outcome as a plain
 *  colored word. Never rendered as an open blind spot. */
export function ResolvedSection({ items }: { items: ResolvedItem[] }) {
  if (items.length === 0) return null;
  return (
    <Card className="avoid-break" style={{ padding: "6px 18px", marginTop: 20 }}>
      {items.map((item, i) => (
        <div
          key={item.key}
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            flexWrap: "wrap",
            padding: "12px 0",
            borderBottom: i === items.length - 1 ? "none" : `1px solid ${C.borderSoft}`,
          }}
        >
          <span style={{ flex: 1, minWidth: 220 }}>
            <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>{item.axisLabel}</span>
            <span
              style={{ ...DM, display: "block", fontSize: 13.5, fontWeight: 600, color: C.ink }}
            >
              {item.title}
            </span>
          </span>
          <span
            style={{
              ...UI,
              display: "inline-flex",
              alignItems: "baseline",
              gap: 6,
              fontSize: 12.5,
              color: C.ink50,
              flex: "0 0 auto",
            }}
          >
            {item.roundLabel && <span>{item.roundLabel} ·</span>}
            <StatusWordText word={item.word} />
          </span>
        </div>
      ))}
    </Card>
  );
}

// ── Loading skeleton ─────────────────────────────────────────────────────────

export function PanelSkeleton() {
  return (
    <div style={{ marginTop: 22 }}>
      <div
        style={{
          background: C.white,
          border: `1px solid ${C.ink10}`,
          borderRadius: 18,
          padding: "20px 24px",
        }}
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}
        >
          <div style={{ display: "flex", gap: 14 }}>
            <Skeleton style={{ width: 56, height: 56, borderRadius: 16 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <Skeleton style={{ width: 220, height: 20 }} />
              <Skeleton style={{ width: 300, height: 13 }} />
            </div>
          </div>
          <Skeleton style={{ width: 120, height: 40 }} />
        </div>
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.ink10}` }}>
          <Skeleton style={{ width: "100%", height: 32 }} />
        </div>
      </div>
      <Skeleton style={{ height: 16, width: 180, borderRadius: 8, marginTop: 34 }} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 12,
          marginTop: 14,
        }}
      >
        <Skeleton style={{ height: 120, borderRadius: 14 }} />
        <Skeleton style={{ height: 120, borderRadius: 14 }} />
        <Skeleton style={{ height: 120, borderRadius: 14 }} />
      </div>
    </div>
  );
}
